/**
 * MY 풀싱크 변경 이력 어댑터
 *
 * MY 백엔드 RESULT_JSON_DATA 구조:
 *   { contactConflicts: [], shareConflicts: [], syncUpdates: [], parsingResults: {} }
 * 각 항목: { sourceType, person_name, identification_type, identification_number,
 *           mismatchFields: string[], before: {}, after: {} }
 *
 * → 공용 ReportDiffPanel 의 DiffChangeItem[] 으로 변환 (SG와 분리된 MY 전용 로직).
 */
import type { DiffChangeItem, DiffFieldChange } from '@shared/components/reportDiff.types';

/** 백엔드 sync-history 응답의 회차 그룹 */
export interface MySyncHistoryGroup {
    reportId: number;
    fromJobIndex: number | null;
    toJobIndex: number | null;
    comparisonDate: string | null;
    reportStatus: string | null;
    details: Array<{
        scrapingType: string | null;
        scrapingName: string | null;
        result: MySyncResult | null;
    }>;
}

interface MySyncEntry {
    sourceType?: string;
    entityCd?: string;
    contactCd?: string;
    person_name?: string;
    identification_type?: string;
    identification_number?: string;
    reason?: string;
    reasonMessage?: string;
    mismatchFields?: string[];
    // 백엔드는 before/after 를 row-슬라이스 "배열" 로 저장한다.
    //   before: 매칭된 ORIGIN 행 슬라이스 배열, after: [스크래핑 행 슬라이스]
    before?: Array<Record<string, unknown>> | Record<string, unknown> | null;
    after?: Array<Record<string, unknown>> | Record<string, unknown> | null;
}

interface MySyncResult {
    contactConflicts?: MySyncEntry[];
    shareConflicts?: MySyncEntry[];
    syncUpdates?: MySyncEntry[];
    parsingResults?: Record<string, unknown>;
}

// MY 관할권은 주주를 "Member" 로 표기 (Entity 상세 탭도 Members)
const SOURCE_TYPE_LABELS: Record<string, string> = {
    director: 'Director',
    secretary: 'Secretary',
    auditor: 'Auditor',
    taxAgent: 'Tax Agent',
    manager: 'Manager',
    officer: 'Director',
    shareholder: 'Member',
    shareholders: 'Member',
    shareholder_detail: 'Member',
    member: 'Member',
    beneficial_owner: 'Beneficial Owner',
    beneficialowner: 'Beneficial Owner',
    controller: 'Beneficial Owner',
};

// sourceType → Entity 상세 화면 탭 값 (클릭 시 이동 대상)
const SOURCE_TYPE_TABS: Record<string, string> = {
    director: 'director',
    secretary: 'secretary',
    auditor: 'auditor',
    taxAgent: 'taxAgent',
    manager: 'manager',
    officer: 'director',
    shareholder: 'shares',
    shareholders: 'shares',
    shareholder_detail: 'shares',
    member: 'shares',
    beneficial_owner: 'controller',
    beneficialowner: 'controller',
    controller: 'controller',
};

function normSourceKey(sourceType?: string): string {
    return String(sourceType || '').toLowerCase().replace(/[\s-]/g, '_');
}

function sourceLabel(sourceType?: string): string {
    if (!sourceType) return '';
    const key = normSourceKey(sourceType);
    return SOURCE_TYPE_LABELS[key] || SOURCE_TYPE_LABELS[key.replace(/_/g, '')] || sourceType;
}

function sourceTab(sourceType?: string): string | undefined {
    if (!sourceType) return undefined;
    const key = normSourceKey(sourceType);
    return SOURCE_TYPE_TABS[key] || SOURCE_TYPE_TABS[key.replace(/_/g, '')];
}

function entryTitle(entry: MySyncEntry): string {
    // top-level 우선, 없으면 before/after 슬라이스에서 추출.
    //   contactCd(GUID)는 헤더에 노출하지 않음(사람 식별 불가) → 이름/식별번호만 사용.
    const pick = (field: string): string => {
        const v = valueForField(entry.after, field) ?? valueForField(entry.before, field);
        return v === null || v === undefined ? '' : String(v).trim();
    };
    return (
        (entry.person_name ? String(entry.person_name).trim() : '') ||
        pick('person_name') ||
        (entry.identification_number ? String(entry.identification_number).trim() : '') ||
        pick('identification_number') ||
        ''
    );
}

/**
 * before/after 는 row-슬라이스 배열(혹은 단일 객체)일 수 있음.
 * 주어진 field 값을 가진 첫 슬라이스에서 값을 추출 (없으면 첫 슬라이스).
 */
function valueForField(raw: unknown, field: string): unknown {
    const slices: Array<Record<string, unknown>> = Array.isArray(raw)
        ? (raw as Array<Record<string, unknown>>)
        : raw && typeof raw === 'object'
            ? [raw as Record<string, unknown>]
            : [];
    for (const s of slices) {
        if (s && Object.prototype.hasOwnProperty.call(s, field) && s[field] !== undefined && s[field] !== null && s[field] !== '') {
            return s[field];
        }
    }
    // 값이 비어도 키가 존재하면 그대로 반환 (첫 슬라이스)
    for (const s of slices) {
        if (s && Object.prototype.hasOwnProperty.call(s, field)) return s[field];
    }
    return undefined;
}

/** 슬라이스 배열/객체에서 데이터 키 합집합 (메타키 _ 제외) */
function unionFieldKeys(raw: unknown): string[] {
    const slices: Array<Record<string, unknown>> = Array.isArray(raw)
        ? (raw as Array<Record<string, unknown>>)
        : raw && typeof raw === 'object'
            ? [raw as Record<string, unknown>]
            : [];
    const keys = new Set<string>();
    for (const s of slices) {
        if (s && typeof s === 'object') {
            for (const k of Object.keys(s)) if (!k.startsWith('_')) keys.add(k);
        }
    }
    return Array.from(keys);
}

/** before/after + mismatchFields → DiffFieldChange[] */
function buildChanges(entry: MySyncEntry): DiffFieldChange[] {
    let fields = Array.isArray(entry.mismatchFields) ? entry.mismatchFields.slice() : [];
    if (fields.length === 0) {
        // mismatchFields 가 없으면 before/after 키 합집합으로 폴백
        const keys = new Set<string>([...unionFieldKeys(entry.before), ...unionFieldKeys(entry.after)]);
        fields = Array.from(keys);
    }

    return fields
        .filter((f) => !f.startsWith('_'))
        .map((field) => ({
            field,
            before: valueForField(entry.before, field),
            after: valueForField(entry.after, field),
        }));
}

function toItem(entry: MySyncEntry, changeType: 'UPDATE' | 'CONFLICT'): DiffChangeItem {
    const label = sourceLabel(entry.sourceType);
    const tab = sourceTab(entry.sourceType);
    return {
        changeType,
        identifier: entryTitle(entry),
        subtitle: label || undefined,
        actionable: !!tab,
        actionTab: tab,
        personId: entry.identification_number || entry.contactCd || undefined,
        changes: buildChanges(entry),
    };
}

/** 단일 RESULT_JSON 객체 → DiffChangeItem[] */
export function buildMyChangeItems(result: MySyncResult | null | undefined): DiffChangeItem[] {
    if (!result || typeof result !== 'object') return [];
    const items: DiffChangeItem[] = [];

    // 실제 적용된 업데이트 (ORIGIN 대비 값 변경)
    for (const u of result.syncUpdates || []) {
        const item = toItem(u, 'UPDATE');
        if (item.changes && item.changes.length > 0) items.push(item);
    }
    // 사용자 확인이 필요한 컨플릭트(연락처/주주)
    for (const c of result.contactConflicts || []) {
        items.push(toItem(c, 'CONFLICT'));
    }
    for (const s of result.shareConflicts || []) {
        items.push(toItem(s, 'CONFLICT'));
    }

    return items;
}

/** 회차 그룹의 모든 detail.result 를 평탄화하여 DiffChangeItem[] */
export function buildGroupChangeItems(group: MySyncHistoryGroup): DiffChangeItem[] {
    const items: DiffChangeItem[] = [];
    for (const d of group.details || []) {
        items.push(...buildMyChangeItems(d.result));
    }
    return items;
}

/** @deprecated FullSync# 라벨 대신 UI에서 Sync date + comparisonDate 사용 */
export function groupLabel(group: MySyncHistoryGroup): string {
    const from = group.fromJobIndex;
    const to = group.toJobIndex;
    if (from != null && to != null) return `FullSync#${from} → #${to}`;
    if (to != null) return `FullSync#${to}`;
    return `Report #${group.reportId}`;
}

// ── 상세 화면 필드 단위 인디케이터용 ─────────────────────────────
/** 한 필드의 싱크 변경 정보 */
export interface SyncFieldChange {
    type: 'UPDATE' | 'CONFLICT';
    before: unknown;
    after: unknown;
    /** 검토 안내(백엔드 reasonMessage) — 예: 주식은 총 주식수만 비교 가능 → 총주식수 확인 */
    note?: string;
}

/** 식별번호 정규화 (대시/공백 제거, 대문자) — 사람 매칭용 */
function normIdNumber(v: unknown): string {
    return String(v ?? '').replace(/[\s-]/g, '').toUpperCase();
}

/**
 * 특정 사람(식별번호 또는 contactCd)에 해당하는 싱크 변경을 필드별 맵으로.
 *   key = 싱크 필드명(identification_type, total_no_of_shares, residential_address ...)
 *   value = { type: UPDATE|CONFLICT, before, after }  (여러 회차 중 최신 우선)
 * 상세 폼은 자신이 렌더하는 필드명으로 map.get() 하여 인디케이터를 표시.
 */
export function buildFieldChangeMap(
    groups: MySyncHistoryGroup[],
    match: { identificationNumber?: string; contactCd?: string }
): Map<string, SyncFieldChange> {
    const map = new Map<string, SyncFieldChange>();
    const idn = normIdNumber(match.identificationNumber);
    const ccd = String(match.contactCd ?? '').trim();
    if (!idn && !ccd) return map;

    const matches = (e: MySyncEntry): boolean => {
        const eIdn = normIdNumber(e.identification_number);
        const eCcd = String(e.contactCd ?? '').trim();
        return (!!idn && !!eIdn && eIdn === idn) || (!!ccd && !!eCcd && eCcd === ccd);
    };

    const apply = (entries: MySyncEntry[] | undefined, type: 'UPDATE' | 'CONFLICT') => {
        for (const e of entries || []) {
            if (!matches(e)) continue;
            const note = type === 'CONFLICT' ? (e.reasonMessage || e.reason || undefined) : undefined;
            for (const c of buildChanges(e)) {
                map.set(c.field, { type, before: c.before, after: c.after, note });
            }
        }
    };

    // 가장 최신 회차(groups[0])의 변경만 반영 — 이전 회차로 폴백/누적하지 않는다.
    //   (이후 재싱크에서 해당 필드 변경이 없으면 인디케이터도 사라지도록; 배너 로직과 일관)
    const g = groups[0];
    if (g) {
        for (const d of g.details || []) {
            const r = d.result;
            if (!r) continue;
            apply(r.syncUpdates, 'UPDATE');
            apply(r.contactConflicts, 'CONFLICT');
            apply(r.shareConflicts, 'CONFLICT');
        }
    }
    return map;
}
